import React, {useCallback, useMemo} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import DatasetFilters from "./DatasetFilters";
import {
  getDatasetSize,
  getDimensionAttributeMap,
  getFormattedDimensionValueLabelFromCodelists
} from "../../utils/dataset";
import {getFilteredTreeWithPaths, getMaxTreeDepth} from "../../utils/tree";

const DatasetFiltersCodelistsWrapper = ({
  nodeCode,
  datasetId,
  dimensions,
  dimensionsInfo,
  dimensionsHidden,
  codelistLists,
  codelistMaps,
  codelistTrees,
  criteria,
  timeDim,
  layout,
  jsonStat,
  labelFormat,
  onSelect,
  onCriteriaShow,
  limit,
  enclosingContainerId,
  localizedTimePeriodFormatMapExternal
}) => {
  const {t} = useTranslation();

  const dimAttributeMap = useMemo(
    () => (jsonStat && (jsonStat?.id || []).length > 0 ? getDimensionAttributeMap(jsonStat, t) : null),
    [jsonStat, t]
  );

  const getIsDimensionAllowed = useCallback(
    dim => (codelistLists?.[dim] || []).length > 1 && !(dimensionsHidden || []).includes(dim),
    [codelistLists, dimensionsHidden]
  );

  const getDimensionLabel = useCallback(
    dimension => dimensions.find(({id}) => id === dimension)?.label || dimension,
    [dimensions]
  );

  const getFilterValues = useCallback(dim => codelistLists[dim], [codelistLists]);

  const getFormattedName = useCallback(
    (dimension, value, labelFormat) => {
      return getFormattedDimensionValueLabelFromCodelists(
        dimension,
        value,
        codelistMaps,
        timeDim,
        labelFormat,
        localizedTimePeriodFormatMapExternal
      );
    },
    [codelistMaps, timeDim, localizedTimePeriodFormatMapExternal]
  );

  const getFormattedTooltip = useCallback(
    (dimension, value, labelFormat) => {
      return getFormattedDimensionValueLabelFromCodelists(
        dimension,
        value,
        codelistMaps,
        timeDim,
        labelFormat,
        localizedTimePeriodFormatMapExternal
      );
    },
    [codelistMaps, timeDim, localizedTimePeriodFormatMapExternal]
  );

  const handleFilterSelect = useCallback(
    (dimension, value) => {
      const orderedValue = Array.isArray(value)
        ? codelistLists[dimension].filter(dimVal => value.includes(dimVal))
        : value;

      onSelect(dimension, orderedValue);
    },
    [codelistLists, onSelect]
  );

  const getIsDatasetExceedingLimit = useCallback(
    (dim, dimValues) => {
      if (!dimensions || limit === null || limit === undefined) {
        return false;
      }
      const tmpLayout = _.cloneDeep(layout);
      if (tmpLayout.primaryDim && tmpLayout.primaryDim.includes(dim)) {
        tmpLayout.primaryDimValues = dimValues;
      } else if (tmpLayout.secondaryDim && tmpLayout.secondaryDim.includes(dim)) {
        tmpLayout.secondaryDimValues = dimValues;
      }
      const datasetSize = getDatasetSize(dimensionsInfo, tmpLayout);
      return datasetSize > limit;
    },
    [dimensions, dimensionsInfo, layout, limit]
  );

  const getTreeFilterValues = useCallback(
    dim => {
      const tree = codelistTrees[dim];
      const dimensionCriteria = criteria[dim];
      if (dimensionCriteria) {
        return getFilteredTreeWithPaths(tree, "children", ({id}) => codelistLists[dim].includes(id));
      } else {
        return tree;
      }
    },
    [codelistTrees, codelistLists, criteria]
  );

  const getDimensionIsHierarchical = useCallback(
    dim => {
      return getMaxTreeDepth(codelistTrees[dim], "children") > 1;
    },
    [codelistTrees]
  );

  return (
    layout && (
      <DatasetFilters
        datasetId={`${nodeCode},${datasetId}`}
        jsonStat={jsonStat}
        timeDim={timeDim}
        layout={layout}
        labelFormat={labelFormat}
        getIsDimensionAllowed={getIsDimensionAllowed}
        getDimensionLabel={getDimensionLabel}
        getFilterValues={getFilterValues}
        getTreeFilterValues={getTreeFilterValues}
        getDimensionIsHierarchical={getDimensionIsHierarchical}
        getFormattedName={getFormattedName}
        getFormattedTooltip={getFormattedTooltip}
        dimAttributeMap={dimAttributeMap}
        onSelect={handleFilterSelect}
        onCriteriaShow={onCriteriaShow}
        getIsDatasetExceedingLimit={getIsDatasetExceedingLimit}
        enclosingContainerId={enclosingContainerId}
        localizedTimePeriodFormatMapExternal={localizedTimePeriodFormatMapExternal}
      />
    )
  );
};

export default DatasetFiltersCodelistsWrapper;
